<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create Affiliate Link'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"
            :delete-options="{
                deleteModalText: deleteModalText,
                buttons: [
                    {
                        buttonText: 'Delete',
                        requestBody: null,
                        buttonDisabled: !canDeleteAffiliateLink,
                        buttonTooltip: canDeleteAffiliateLink ? '' : 'Affiliate Link in use',
                    },
                ],
            }"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :show-preview-button="false"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-text
            name="name"
            label="* Name (include name of casino)"
            v-model="form.name"
            :error="errors.name ? errors.name[0] : null"
        />
        <input-text
            name="affiliate_url"
            label="* Affiliate URL"
            v-model="form.affiliate_url"
            class="mt-2"
            :error="errors.affiliate_url ? errors.affiliate_url[0] : null"
        />
        <input-text
            name="button_text"
            label="* Button Text"
            v-model="form.button_text"
            class="mt-2"
            :error="errors.button_text ? errors.button_text[0] : null"
        />
         <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">* Casino</p>
            <reference
                create-text="Create new casino and link casino to this page"
                link-text="Link an existing casino"
                model-data="Casino"
                :should-open-new-window=true
                v-model="form.casino"
                :createable="false"
                :deletable="false"
                :error="errors.casino_id ? errors.casino_id[0] : null"
            ></reference>
        </div>
        <input-text
            name="pretty_link"
            label="* Pretty Link"
            v-model="form.pretty_link"
            class="mt-2"
            :disabled="true"
            :error="errors.pretty_link ? errors.pretty_link[0] : null"
        />

        <input-checkbox
            name="adblock_workaround"
            label="Use adblock workaround?"
            class="mt-3"
            v-model="form.adblock_workaround"
            :error="errors.adblock_workaround ? errors.adblock_workaround[0] : null"
        />
        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import {v4 as uuidv4} from 'uuid';
import crudForm from "../mixins/crudForm";

export default {
    name: "AffiliateLinkForm",
    mixins: [crudForm],
    props: {
        prettyLinkRoot: {
            type: String,
            default: '/mw/ob',
        }
    },
    data() {
        return {
            form: {
                name: this.getFormData("name"),
                pretty_link_uuid: this.getFormData("pretty_link_uuid"),
                pretty_link: this.prettyLinkRoot + this.getFormData("pretty_link"),
                affiliate_url: this.getFormData("affiliate_url"),
                button_text: this.getFormData("button_text", "Claim"),
                casino: this.getFormData("casino"),
                casino_id: this.getFormData("casino_id"),
                adblock_workaround: this.getFormData("adblock_workaround", false),
            },
        };
    },
    methods: {
        slugify(text){
            if(!text) return "";
            return text.toString().toLowerCase()
                .replace(/\s+/g, '-') // Replace spaces with -
                .replace(/&/g, '-and-') // Replace & with 'and'
                .replace(/[^\w\-]+/g, '') // Remove all non-word chars
                .replace(/--+/g, '-'); // Replace multiple - with single -
        },
        buildPrettyLink(...slugs){
            return `${this.prettyLinkRoot}/${slugs.filter(e=>e).map(this.slugify).join('/')}/${this.form.pretty_link_uuid}/`;
        }
    },
    watch: {
        "form.casino": function(newValue){
            if (this.form.casino_id != newValue?.id) {
                this.form.casino_id = newValue?.id;
                const casinoName = newValue?.name ?? 'casino'
                this.form.pretty_link_uuid = uuidv4();
                this.form.pretty_link = this.buildPrettyLink(casinoName)
            }
        },
    },
    computed: {
        canDeleteAffiliateLink(){
            if (!this.data) {
                // data will be null when creating
                return true;
            }
            let usage = this.data.usage;
            return (
                usage.bonuses.length
                + usage.casinoReviewsTwoColFourTable.length
                + usage.links.length
                + Object.keys(usage.textContent).length
            ) === 0;
        },
        deleteModalText(){
            if (this.canDeleteAffiliateLink) return '';

            let usage = this.data.usage;
            let hasSoftDeleted = false;

            let bonuses = usage.bonuses.map(e => {
                let deletedIndicator = '';
                if (e.trashed) {
                    deletedIndicator = ' <span class="text-red-300">(Deleted)</span>';
                    hasSoftDeleted = true;
                }
                return `<a target="_blank" href="${e.route}">${e.route}${deletedIndicator}</a>`;
            }).join('<br>');

            let casinoReviewsTwoColFourTable = usage.casinoReviewsTwoColFourTable.map(e => {
                let deletedIndicator = '';
                if (e.trashed) {
                    deletedIndicator = ' <span class="text-red-300">(Deleted)</span>';
                    hasSoftDeleted = true;
                }
                return `<a target="_blank" href="${e.route}">${e.route}${deletedIndicator}</a>`;
            }).join('<br>');

            let links = usage.links.map(e => {
                let deletedIndicator = '';
                if (e.trashed) {
                    deletedIndicator = ' <span class="text-red-300">(Deleted)</span>';
                    hasSoftDeleted = true;
                }
                return `<a target="_blank" href="${e.route}">${e.route}${deletedIndicator}</a>`;
            }).join('<br>');

            let textContent = Object.values(usage.textContent).map(e => {
                let deletedIndicator = '';
                if (e.trashed) {
                    deletedIndicator = ' <span class="text-red-300">(Deleted)</span>';
                    hasSoftDeleted = true;
                }
                return `<a target="_blank" href="${e.route}">${e.route}${deletedIndicator}</a>`;
            }).join('<br>');

            let usages = [
                (bonuses.length ? `<strong>Bonuses:</strong><br>${bonuses}` : ''),
                (casinoReviewsTwoColFourTable.length ? `<strong>Casino Reviews 2/4 Table:</strong><br>${casinoReviewsTwoColFourTable}` : ''),
                (links.length ? `<strong>Links:</strong><br>${links}` : ''),
                (textContent.length ? `<strong>In text content:</strong><br>${textContent}` : ''),
            ].filter(e => e !== '');

            let deletedMessage = hasSoftDeleted ? '<span class="text-red-300">To edit deleted documents, please reach out to a developer.</span><br><br>' : '';

            return `<strong>Affiliate Link is in use.</strong><br>
                    To delete the Affiliate Link please remove it from these places:<br><br>
                    ${deletedMessage}
                    ${usages.join('<br><br>')}`;
        },
    },
};
</script>
